/**
 * GROCERY ADD-ON ON A PROPERTY — the super-admin control, and nothing else.
 *
 * This screen is the CONTROL described in Part B3/B8: one switch per building,
 * off by default, with a confirmation and an audit entry; a link step that
 * attaches the seeded demo households to the property's own units; and the
 * property-side summary, shown here precisely so an operator can see that it
 * carries aggregates and never a household.
 *
 * It does NOT build the resident tile or the property manager panel — those are
 * the plan in GROCERY_PROPERTY_ADDON_PLAN.md, deliberately not built yet.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Link2, Unlink, ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';

const Row = ({ label, value, testId }) => (
    <div className="flex items-baseline justify-between gap-4 border-b border-slate-200/70 py-1.5 last:border-0 dark:border-slate-700/60">
        <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100" data-testid={testId}>
            {value}
        </span>
    </div>
);

export default function GroceryPropertyAddon() {
    const { t } = useTranslation();
    const [props_, setProps] = useState([]);
    const [sel, setSel] = useState(null);
    const [state, setState] = useState(null);
    const [summary, setSummary] = useState(null);
    const [busy, setBusy] = useState(false);
    const [ask, setAsk] = useState(null);      // 'toggle' | 'attach' | 'detach'
    const [err, setErr] = useState('');

    const loadOptions = useCallback(async () => {
        const { data } = await api.get('/grocery/property/options');
        setProps(data.properties || []);
        if (!sel && (data.properties || []).length) setSel(data.properties[0].location_id);
    }, [sel]);

    const loadState = useCallback(async (locationId) => {
        if (!locationId) return;
        const [a, s] = await Promise.all([
            api.get('/grocery/property/addon', { params: { location_id: locationId } }),
            api.get('/grocery/property/summary', { params: { location_id: locationId } }),
        ]);
        setState(a.data);
        setSummary(s.data);
    }, []);

    useEffect(() => { loadOptions().catch((e) => setErr(String(e))); }, [loadOptions]);
    useEffect(() => { loadState(sel).catch((e) => setErr(String(e))); }, [sel, loadState]);

    const run = async (what) => {
        setBusy(true);
        setErr('');
        try {
            if (what === 'toggle') {
                await api.post('/grocery/property/addon',
                    { location_id: sel, enabled: !state?.enabled, confirm: true });
            } else if (what === 'attach') {
                await api.post('/grocery/property/attach', { location_id: sel, confirm: true });
            } else {
                await api.post('/grocery/property/detach', { confirm: true });
            }
            await loadOptions();
            await loadState(sel);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        }
        setBusy(false);
        setAsk(null);
    };

    const chosen = props_.find((p) => p.location_id === sel);

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-4" data-testid="grocery-property-addon-page">
            <header className="space-y-1">
                <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-slate-50">
                    <Building2 className="h-6 w-6" />{t('groceryAddon.title')}
                </h1>
                <p className="text-sm text-slate-600 dark:text-slate-300">{t('groceryAddon.lead')}</p>
            </header>

            {err ? (
                <p className="flex items-center gap-2 rounded-xl bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-200"
                    data-testid="grocery-addon-error">
                    <AlertTriangle className="h-4 w-4" />{err}
                </p>
            ) : null}

            <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500">
                    {t('groceryAddon.property')}
                </label>
                <select value={sel || ''} onChange={(e) => setSel(e.target.value)}
                    data-testid="grocery-addon-property-select"
                    className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 text-sm dark:border-slate-600 dark:bg-slate-800">
                    {props_.map((p) => (
                        <option key={p.location_id} value={p.location_id}>
                            {p.organization_name} — {p.location_name} ({p.units} {t('groceryAddon.units')})
                        </option>
                    ))}
                </select>

                <div className="mt-4 flex items-center justify-between gap-4 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
                    <div>
                        <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                            {t('groceryAddon.switch')}
                        </p>
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                            {t('groceryAddon.switchHint')}
                        </p>
                    </div>
                    <Switch checked={!!state?.enabled} disabled={busy || !sel}
                        onCheckedChange={() => setAsk('toggle')}
                        data-testid="grocery-addon-switch" />
                </div>

                <div className="mt-4 space-y-1">
                    <Row label={t('groceryAddon.status')}
                        value={state?.enabled ? t('groceryAddon.on') : t('groceryAddon.off')}
                        testId="grocery-addon-status" />
                    <Row label={t('groceryAddon.unitsOnProperty')} value={chosen?.units ?? '—'}
                        testId="grocery-addon-units" />
                    <Row label={t('groceryAddon.linkedBuilding')}
                        value={state?.linked_building?.name || t('groceryAddon.none')}
                        testId="grocery-addon-linked" />
                    <Row label={t('groceryAddon.attached')} value={state?.attached_households ?? 0}
                        testId="grocery-addon-attached" />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                    <Button onClick={() => setAsk('attach')} disabled={busy || !state?.enabled}
                        className="min-h-[44px] gap-2" data-testid="grocery-addon-attach">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                        {t('groceryAddon.attach')}
                    </Button>
                    <Button variant="outline" onClick={() => setAsk('detach')}
                        disabled={busy || !state?.attached_households}
                        className="min-h-[44px] gap-2" data-testid="grocery-addon-detach">
                        <Unlink className="h-4 w-4" />{t('groceryAddon.detach')}
                    </Button>
                </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
                data-testid="grocery-addon-summary">
                <h2 className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-slate-100">
                    <ShieldCheck className="h-5 w-5" />{t('groceryAddon.summaryTitle')}
                </h2>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    {t('groceryAddon.summaryHint')}
                </p>
                {summary?.suppressed ? (
                    <p className="mt-3 text-sm font-semibold text-amber-700 dark:text-amber-300"
                        data-testid="grocery-addon-suppressed">
                        {t('groceryAddon.suppressed', { n: summary.households })}
                    </p>
                ) : (
                    <div className="mt-3 space-y-1">
                        <Row label={t('groceryAddon.households')} value={summary?.households ?? 0}
                            testId="grocery-addon-sum-households" />
                        <Row label={t('groceryAddon.ordersTotal')}
                            value={summary?.metrics?.orders_total ?? 0}
                            testId="grocery-addon-sum-orders" />
                        <Row label={t('groceryAddon.perHousehold')}
                            value={summary?.metrics?.orders_per_household ?? 0}
                            testId="grocery-addon-sum-per" />
                        <Row label={t('groceryAddon.deliveryDays')}
                            value={Object.entries(summary?.metrics?.delivery_days || {})
                                .map(([d, n]) => `${d} ${n}`).join(' · ') || '—'}
                            testId="grocery-addon-sum-days" />
                    </div>
                )}
            </section>

            <Dialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)}>
                <DialogContent data-testid="grocery-addon-confirm">
                    <DialogHeader>
                        <DialogTitle>{t(`groceryAddon.confirm.${ask || 'toggle'}Title`)}</DialogTitle>
                        <DialogDescription>
                            {t(`groceryAddon.confirm.${ask || 'toggle'}Body`)}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAsk(null)}
                            className="min-h-[44px]" data-testid="grocery-addon-confirm-cancel">
                            {t('groceryAddon.cancel')}
                        </Button>
                        <Button onClick={() => run(ask)} disabled={busy}
                            className="min-h-[44px]" data-testid="grocery-addon-confirm-ok">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('groceryAddon.iAmSure')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
